import React, { useState, useEffect, useCallback } from 'react';
import { collection, query, orderBy, limit, where, getDocs, startAfter } from 'firebase/firestore';
import { db } from '../firebase'; // Ajusta la ruta según tu configuración
import WordCloudDisplay from './WordCloudDisplay';
import DemographicAnalysis from './DemographicAnalysis';


const WordCloudStats = () => {
  const [conversations, setConversations] = useState([]);
  const [startDate, setStartDate] = useState(() => 
    new Date(new Date().getFullYear(), new Date().getMonth(), 1)
  );
  const [endDate, setEndDate] = useState(() => 
    new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0, 23, 59, 59, 999)
  );
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [lastDocument, setLastDocument] = useState(null);
  const [hasMore, setHasMore] = useState(true);

  const BATCH_SIZE = 50;

  // Función optimizada para cargar datos con filtro de "internal"
  const fetchConversations = useCallback(async (isInitial = false) => {
    if (isInitial) {
      setLoading(true);
      setConversations([]);
      setLastDocument(null);
      setHasMore(true);
    } else {
      setLoadingMore(true);
    }

    try {
      const conversationsRef = collection(db, 'conversations');
      let q = query(
        conversationsRef,
        orderBy('timestamp', 'desc'),
        limit(BATCH_SIZE)
      );

      if (startDate && endDate) {
        q = query(
          conversationsRef,
          where('timestamp', '>=', startDate),
          where('timestamp', '<=', endDate),
          orderBy('timestamp', 'desc'),
          limit(BATCH_SIZE)
        );
      }

      if (!isInitial && lastDocument) {
        q = query(
          conversationsRef,
          where('timestamp', '>=', startDate),
          where('timestamp', '<=', endDate),
          orderBy('timestamp', 'desc'),
          startAfter(lastDocument),
          limit(BATCH_SIZE)
        );
      }

      const snapshot = await getDocs(q);
      const newConvos = snapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        .filter(conv => {
          const tags = conv.tags || [];
          return !tags.includes('internal') && !tags.includes('internal_check');
        });

      if (isInitial) {
        setConversations(newConvos);
      } else {
        setConversations(prev => [...prev, ...newConvos]);
      }

      if (snapshot.docs.length > 0) {
        setLastDocument(snapshot.docs[snapshot.docs.length - 1]);
        setHasMore(snapshot.docs.length === BATCH_SIZE);
      } else {
        setHasMore(false);
      }

    } catch (error) {
      console.error("Error fetching conversations: ", error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [startDate, endDate, lastDocument]);

  useEffect(() => {
    fetchConversations(true);
  }, [startDate, endDate]);

  const formatDateForInput = useCallback((date) => {
    if (!date) return '';
    return date.toISOString().split('T')[0];
  }, []);

  const handleDateChange = useCallback((e) => {
    const { name, value } = e.target;
    const [year, month, day] = value.split('-').map(Number);
    
    if (name === 'startDate') {
      const newStartDate = new Date(year, month - 1, day, 0, 0, 0, 0);
      setStartDate(newStartDate);
    } else {
      const newEndDate = new Date(year, month - 1, day, 23, 59, 59, 999);
      setEndDate(newEndDate);
    }
  }, []);

  const setDateRangePreset = useCallback((preset) => {
    const now = new Date();
    let start, end;

    switch (preset) {
      case 'today':
        start = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
        end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
        break;
      case 'yesterday':
        const yesterday = new Date(now);
        yesterday.setDate(now.getDate() - 1);
        start = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate(), 0, 0, 0, 0);
        end = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate(), 23, 59, 59, 999);
        break;
      case 'week':
        const firstDayOfWeek = new Date(now);
        firstDayOfWeek.setDate(now.getDate() - now.getDay() + (now.getDay() === 0 ? -6 : 1));
        start = new Date(firstDayOfWeek.getFullYear(), firstDayOfWeek.getMonth(), firstDayOfWeek.getDate(), 0, 0, 0, 0);
        const lastDayOfWeek = new Date(start);
        lastDayOfWeek.setDate(start.getDate() + 6);
        end = new Date(lastDayOfWeek.getFullYear(), lastDayOfWeek.getMonth(), lastDayOfWeek.getDate(), 23, 59, 59, 999);
        break;
      case 'month':
        start = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
        end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
        break;
      case 'year':
        start = new Date(now.getFullYear(), 0, 1, 0, 0, 0, 0);
        end = new Date(now.getFullYear(), 11, 31, 23, 59, 59, 999);
        break;
      default: return;
    }
    setStartDate(start);
    setEndDate(end);
  }, []);


  return (
    <div className="bg-white shadow-lg rounded-lg p-6 w-full max-w-6xl mx-auto">
      <h3 className="text-2xl font-bold mb-6 text-gray-800 border-b pb-2">
        Análisis de Palabras - Mensajes de Usuarios
      </h3>

      {/* Controles de fecha */}
      <div className="mb-6 p-4 border border-gray-200 rounded-lg bg-gray-50">
        <h4 className="font-bold text-lg mb-3 text-gray-700">
          Seleccionar Rango de Fechas
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-1">
              Fecha de Inicio:
            </label>
            <input 
              type="date" 
              id="startDate" 
              name="startDate" 
              className="p-2 border border-gray-300 rounded-md w-full focus:ring-2 focus:ring-blue-500" 
              value={formatDateForInput(startDate)} 
              onChange={handleDateChange} 
            />
          </div>
          <div>
            <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 mb-1">
              Fecha Final:
            </label>
            <input 
              type="date" 
              id="endDate" 
              name="endDate" 
              className="p-2 border border-gray-300 rounded-md w-full focus:ring-2 focus:ring-blue-500" 
              value={formatDateForInput(endDate)} 
              onChange={handleDateChange} 
            />
          </div>
        </div>
        <div className="flex items-center space-x-2 flex-wrap">
          {[
            { key: 'today', label: 'Hoy' },
            { key: 'yesterday', label: 'Ayer' },
            { key: 'week', label: 'Semana' },
            { key: 'month', label: 'Mes' },
            { key: 'year', label: 'Año' }
          ].map(preset => (
            <button 
              key={preset.key}
              onClick={() => setDateRangePreset(preset.key)} 
              className="px-3 py-1 text-sm bg-gray-200 rounded hover:bg-gray-300 mb-2 transition-colors"
            >
              {preset.label}
            </button>
          ))}
        </div>
      </div>

      {/* Información de estado */}
      <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div className="text-sm text-blue-700 mb-2 sm:mb-0">
            <span className="font-semibold">Conversaciones analizadas:</span> {conversations.length}
            <span className="ml-4 text-blue-600">
              (Excluyendo conversaciones con tag "internal")
            </span>
          </div>
          {hasMore && (
            <button 
              onClick={() => fetchConversations(false)}
              disabled={loadingMore}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-400 transition-colors text-sm"
            >
              {loadingMore ? 'Cargando...' : 'Cargar más datos'}
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-pulse text-center">
            <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-500">Cargando datos de conversaciones...</p>
          </div>
        </div>
      ) : conversations.length > 0 ? (
        <>
          <WordCloudDisplay conversations={conversations} />
          <DemographicAnalysis conversations={conversations} />
        </>
      ) : (
        <div className="flex justify-center items-center h-64">
          <div className="text-center">
            <p className="text-gray-500 text-lg mb-2">
              📭 No se encontraron mensajes de usuario
            </p>
            <p className="text-gray-400 text-sm">
              en el rango de fechas seleccionado (excluyendo conversaciones internas)
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default WordCloudStats;
